import { useEffect, useRef } from "react";
import { getToken, API_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * useRealtime — connects to `/api/ws?token=<jwt>` and dispatches CustomEvents
 * of the shape `simpler:<event>` with the payload as detail.
 *
 * Consumers listen with:
 *   window.addEventListener("simpler:alert.created", (e) => ...);
 */
export function useRealtime() {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    let pingInterval = null;

    const connect = () => {
      const token = getToken();
      if (!token) return;

      const wsUrl = API_URL.replace(/^http/, "ws") + `/ws?token=${encodeURIComponent(token)}`;

      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        pingInterval = setInterval(() => {
          try { ws.send("ping"); } catch { /* noop */ }
        }, 25000);
      };

      ws.onmessage = (evt) => {
        try {
          if (evt.data === "pong") return;
          const data = JSON.parse(evt.data);
          if (!data.event) return;
          window.dispatchEvent(new CustomEvent(`simpler:${data.event}`, { detail: data.payload }));
        } catch { /* ignore */ }
      };

      ws.onclose = () => {
        if (pingInterval) clearInterval(pingInterval);
        pingInterval = null;
        if (cancelled) return;
        // Reconnect with exponential backoff, cap 30s
        retryRef.current += 1;
        const wait = Math.min(30000, 1500 * 2 ** Math.min(retryRef.current, 5));
        setTimeout(() => { if (!cancelled) connect(); }, wait);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (pingInterval) clearInterval(pingInterval);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [user]);
}
